import { Controller } from "@hotwired/stimulus"

/**
 * RulesBuilderController - Visual rule builder for Library Collections
 *
 * Manages a dynamic list of rules that serialize to JSON for the backend.
 * Each rule has: attribute, operator, value, data_type
 *
 * Features:
 * - Auto-infers data type based on attribute selection
 * - Filters operators based on data type (array vs comparison)
 * - Adapts value input based on data type (number, text, array pills)
 * - Serializes to hidden JSON field on any change
 * - Auto-refreshes preview after changes (debounced)
 */
export default class extends Controller {
  static targets = ["rulesContainer", "hiddenInput", "template"]
  static values = {
    previewUrl: String,       // URL to fetch preview from
    previewFrameId: String,   // Turbo frame ID to update
    debounceMs: { type: Number, default: 1500 },
    categories: { type: Array, default: [] },    // [[label, id], ...]
    yearLevels: { type: Array, default: [] }     // [[label, id], ...]
  }

  // Attributes that use checkbox pills instead of text input
  static CHECKBOX_ATTRIBUTES = ["categories", "year_levels"]

  // Attribute -> default data type mapping
  static ATTRIBUTE_DATA_TYPES = {
    average_rating: "decimal",
    student_average_rating: "decimal",
    teacher_average_rating: "decimal",
    completed_count: "integer",
    feedback_count: "integer",
    student_feedback_count: "integer",
    teacher_feedback_count: "integer",
    lesson_created_at: "date",
    lesson_version_created_at: "date",
    full_version_number: "version",
    categories: "array",
    year_levels: "array"
  }

  // Operators available for each data type category
  static COMPARISON_OPERATORS = [
    { value: "equals", label: "equals" },
    { value: "greater_than", label: "greater than" },
    { value: "less_than", label: "less than" },
    { value: "greater_than_or_equal", label: ">=" },
    { value: "less_than_or_equal", label: "<=" }
  ]

  static ARRAY_OPERATORS = [
    { value: "includes_any", label: "includes any" },
    { value: "random_from", label: "random from (weekly)" }
  ]

  connect() {
    this.loadExistingRules()
    this.previewTimeout = null

    this.form = this.element.closest("form")
    if (this.form) {
      this.handleMarketChange = this.handleMarketChange.bind(this)
      this.form.addEventListener("change", this.handleMarketChange)
    }

    // Auto-load preview on edit page if rules exist
    if (this.hiddenInputTarget.value && this.hiddenInputTarget.value !== "[]") {
      this.refreshPreview()
    }
  }

  disconnect() {
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout)
    }
    if (this.form && this.handleMarketChange) {
      this.form.removeEventListener("change", this.handleMarketChange)
    }
  }

  handleMarketChange(event) {
    if (event.target.matches('input[name="library_collection[supported_markets][]"]')) {
      this.schedulePreviewRefresh()
    }
  }

  loadExistingRules() {
    const existingValue = this.hiddenInputTarget.value
    if (!existingValue || existingValue.trim() === "" || existingValue === "[]") {
      return
    }

    try {
      const rules = JSON.parse(existingValue)
      if (Array.isArray(rules)) {
        rules.forEach(rule => this.addRuleRow(rule))
      }
    } catch (e) {
      console.error("Error parsing existing rules:", e)
    }
  }

  addRule(event) {
    event?.preventDefault()
    this.addRuleRow()
  }

  addRuleRow(existingRule = null) {
    const template = this.templateTarget.content.cloneNode(true)
    const ruleRow = template.querySelector("[data-rule-row]")

    if (existingRule) {
      this.populateRuleRow(ruleRow, existingRule)
    }

    this.rulesContainerTarget.appendChild(template)

    // Initialize the new row - pass existing operator to preserve it after rebuilding options
    const newRow = this.rulesContainerTarget.lastElementChild
    this.updateOperatorsForRow(newRow, existingRule?.operator)
    this.updateValueInputForRow(newRow)
  }

  populateRuleRow(row, rule) {
    const attributeSelect = row.querySelector("[data-attribute]")
    const operatorSelect = row.querySelector("[data-operator]")
    const dataTypeSelect = row.querySelector("[data-data-type]")
    const valueInput = row.querySelector("[data-value]")

    if (rule.attribute) attributeSelect.value = rule.attribute
    if (rule.data_type) dataTypeSelect.value = rule.data_type
    if (rule.operator) operatorSelect.value = rule.operator

    // Handle value - could be array or scalar
    if (rule.value !== undefined && rule.value !== null) {
      if (Array.isArray(rule.value)) {
        valueInput.value = JSON.stringify(rule.value)
        valueInput.dataset.isArray = "true"
        valueInput.dataset.selectedValues = JSON.stringify(rule.value)
      } else {
        valueInput.value = rule.value
      }
    }
  }

  removeRule(event) {
    event.preventDefault()
    const row = event.target.closest("[data-rule-row]")
    row.remove()
    this.serialize()
  }

  attributeChanged(event) {
    const row = event.target.closest("[data-rule-row]")
    const attribute = event.target.value
    const dataTypeSelect = row.querySelector("[data-data-type]")
    const valueInput = row.querySelector("[data-value]")

    // Auto-infer data type
    const inferredType = this.constructor.ATTRIBUTE_DATA_TYPES[attribute]
    if (inferredType) {
      dataTypeSelect.value = inferredType
    }

    // Clear selected values when attribute changes to prevent stale data
    // (e.g., category IDs persisting when switching to year_levels)
    if (valueInput) {
      valueInput.dataset.selectedValues = "[]"
      valueInput.value = ""
    }

    this.updateOperatorsForRow(row)
    this.updateValueInputForRow(row)
    this.serialize()
  }

  operatorChanged(event) {
    this.serialize()
  }

  valueChanged(event) {
    this.serialize()
  }

  updateOperatorsForRow(row, existingOperator = null) {
    const dataTypeSelect = row.querySelector("[data-data-type]")
    const operatorSelect = row.querySelector("[data-operator]")
    // Use existing operator if provided (for loading saved rules), otherwise use current select value
    const currentOperator = existingOperator || operatorSelect.value
    const dataType = dataTypeSelect.value

    const operators = dataType === "array"
      ? this.constructor.ARRAY_OPERATORS
      : this.constructor.COMPARISON_OPERATORS

    operatorSelect.innerHTML = operators
      .map(op => `<option value="${op.value}">${op.label}</option>`)
      .join("")

    const validValues = operators.map(op => op.value)
    operatorSelect.value = validValues.includes(currentOperator) ? currentOperator : operators[0].value
  }

  updateValueInputForRow(row) {
    const attributeSelect = row.querySelector("[data-attribute]")
    const dataTypeSelect = row.querySelector("[data-data-type]")
    const valueInput = row.querySelector("[data-value]")
    const valueWrapper = row.querySelector("[data-value-wrapper]")
    const checkboxWrapper = row.querySelector("[data-checkbox-wrapper]")
    const attribute = attributeSelect.value
    const dataType = dataTypeSelect.value

    // Check if this attribute uses checkbox pills
    const useCheckboxes = this.constructor.CHECKBOX_ATTRIBUTES.includes(attribute)

    if (useCheckboxes && checkboxWrapper) {
      // Show checkbox pills, hide text input
      valueWrapper.classList.add("hidden")
      checkboxWrapper.classList.remove("hidden")

      const options = attribute === "categories" ? this.categoriesValue : this.yearLevelsValue
      const selectedValues = this.getSelectedValues(valueInput)

      this.renderCheckboxPills(checkboxWrapper, options, selectedValues, row)
    } else {
      // Show text input, hide checkboxes
      valueWrapper.classList.remove("hidden")
      if (checkboxWrapper) checkboxWrapper.classList.add("hidden")

      // Update input type and placeholder based on data type
      switch (dataType) {
        case "integer":
          valueInput.type = "number"
          valueInput.step = "1"
          valueInput.placeholder = "e.g., 100"
          break
        case "decimal":
          valueInput.type = "number"
          valueInput.step = "0.1"
          valueInput.placeholder = "e.g., 4.5"
          break
        case "date":
          valueInput.type = "number"
          valueInput.step = "1"
          valueInput.placeholder = "days ago (e.g., 90)"
          break
        default:
          valueInput.type = "text"
          valueInput.placeholder = "value"
      }
    }
  }

  renderCheckboxPills(container, options, selectedValues, row) {
    // Normalize selectedValues to strings for consistent comparison
    const normalizedSelected = selectedValues.map(String)
    const pillsHtml = options.map(([label, value]) => {
      const isSelected = normalizedSelected.includes(String(value))

      return `
        <button type="button"
                data-action="click->rules-builder#toggleCheckboxPill"
                data-value="${this.escapeHtml(String(value))}"
                data-selected="${isSelected}"
                class="inline-flex h-7 items-center rounded-2xl border border-contrast-medium bg-surface px-3 text-label-sm text-content-primary transition-colors hover:bg-surface-muted data-[selected=true]:border-inverse data-[selected=true]:bg-inverse data-[selected=true]:text-content-inverse">
          ${this.escapeHtml(label)}
        </button>
      `
    }).join("")

    container.innerHTML = `<div class="flex flex-wrap gap-1.5">${pillsHtml}</div>`
  }

  toggleCheckboxPill(event) {
    event.preventDefault()
    const button = event.currentTarget
    const row = button.closest("[data-rule-row]")
    const valueInput = row.querySelector("[data-value]")
    const rawValue = button.dataset.value
    const value = isNaN(rawValue) ? rawValue : Number(rawValue)
    const isSelected = button.dataset.selected === "true"

    let selectedValues = this.getSelectedValues(valueInput)
    const newSelected = !isSelected

    selectedValues = newSelected
      ? [...selectedValues, value]
      : selectedValues.filter(v => String(v) !== String(value))

    button.dataset.selected = String(newSelected)
    valueInput.dataset.selectedValues = JSON.stringify(selectedValues)
    this.serialize()
  }

  getSelectedValues(valueInput) {
    try {
      const stored = valueInput.dataset.selectedValues
      return stored ? JSON.parse(stored) : []
    } catch (e) {
      return []
    }
  }

  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }

  serialize() {
    const rows = this.rulesContainerTarget.querySelectorAll("[data-rule-row]")
    const rules = []

    rows.forEach(row => {
      const attribute = row.querySelector("[data-attribute]")?.value
      const operator = row.querySelector("[data-operator]")?.value
      const dataType = row.querySelector("[data-data-type]")?.value
      const valueInput = row.querySelector("[data-value]")

      const value = this.constructor.CHECKBOX_ATTRIBUTES.includes(attribute)
        ? valueInput?.dataset.selectedValues || "[]"
        : valueInput?.value

      // Skip incomplete or empty rules
      if (!attribute || !operator || !dataType || value === "" || value === undefined) return

      const parsedValue = this.parseValue(value, dataType)
      if (Array.isArray(parsedValue) && parsedValue.length === 0) return

      rules.push({ attribute, operator, value: parsedValue, data_type: dataType })
    })

    this.hiddenInputTarget.value = rules.length > 0 ? JSON.stringify(rules) : ""
    this.schedulePreviewRefresh()
  }

  schedulePreviewRefresh() {
    // Only refresh if we have a preview URL and frame configured
    if (!this.hasPreviewUrlValue || !this.hasPreviewFrameIdValue) {
      return
    }

    // Clear any pending refresh
    if (this.previewTimeout) {
      clearTimeout(this.previewTimeout)
    }

    // Schedule new refresh after debounce period
    this.previewTimeout = setTimeout(() => {
      this.refreshPreview()
    }, this.debounceMsValue)
  }

  async refreshPreview() {
    const frame = document.getElementById(this.previewFrameIdValue)
    if (!frame) return

    // Show loading state
    frame.innerHTML = `
      <div class="flex items-center gap-2 text-body-sm text-content-secondary">
        <svg class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        Refreshing preview...
      </div>
    `

    // Collect current form data
    const rules = this.hiddenInputTarget.value
    const supportedMarkets = this.collectSupportedMarkets()

    try {
      const response = await fetch(this.previewUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "text/vnd.turbo-stream.html, text/html",
          "X-CSRF-Token": this.csrfToken
        },
        body: JSON.stringify({
          rules: rules,
          supported_markets: supportedMarkets,
          frame: this.previewFrameIdValue
        })
      })

      if (response.ok) {
        const html = await response.text()
        // Extract the turbo-frame content and update
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, "text/html")
        const newFrame = doc.querySelector(`turbo-frame#${this.previewFrameIdValue}`)
        if (newFrame) {
          frame.innerHTML = newFrame.innerHTML
        } else {
          frame.innerHTML = html
        }
      } else {
        frame.innerHTML = `<p class="text-body-sm text-content-negative">Error loading preview</p>`
      }
    } catch (error) {
      console.error("Preview fetch error:", error)
      frame.innerHTML = `<p class="text-body-sm text-content-negative">Error loading preview</p>`
    }
  }

  collectSupportedMarkets() {
    if (!this.form) return []

    const checkboxes = this.form.querySelectorAll('input[name="library_collection[supported_markets][]"]:checked')
    return Array.from(checkboxes).map(cb => cb.value)
  }

  get csrfToken() {
    const meta = document.querySelector('meta[name="csrf-token"]')
    return meta ? meta.content : ""
  }

  parseValue(value, dataType) {
    switch (dataType) {
      case "integer":
        return parseInt(value, 10) || 0
      case "decimal":
        return parseFloat(value) || 0
      case "date":
        return parseInt(value, 10) || 0
      case "array":
        // Try to parse as JSON array, otherwise split by comma
        try {
          const parsed = JSON.parse(value)
          if (Array.isArray(parsed)) return parsed
        } catch (e) {
          // Not valid JSON, try comma split
        }
        return value.split(",").map(v => v.trim()).filter(v => v)
      default:
        return value
    }
  }
}
